import React, { useState } from "react";
import LibraryGuide from "@/components/LibraryGuide";
import { BookOpen, Question, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const Section = ({ title, children, testid }) => (
  <div className="bg-white border border-slate-200 p-6 mb-4" data-testid={testid}>
    <h3 className="font-display text-xl font-bold">{title}</h3>
    <div className="mt-3 text-sm text-slate-700 leading-relaxed space-y-2">{children}</div>
  </div>
);

export default function HelpPage() {
  const [guideOpen, setGuideOpen] = useState(false);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl" data-testid="help-page">
      <div className="label-micro">Training</div>
      <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Help & Tutorials</h1>
      <p className="text-sm text-slate-600 mt-2 max-w-2xl">Everything your team needs to know to run TNAI Sales Suite confidently.</p>

      <div className="mt-6 sm:mt-8 bg-gradient-to-r from-slate-900 to-slate-800 text-white p-5 sm:p-6 rounded-sm relative overflow-hidden">
        <div className="relative z-10 flex items-start sm:items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="label-micro text-slate-300">Featured tutorial</div>
            <h2 className="font-display text-2xl font-bold mt-1">Managing the Content Libraries</h2>
            <p className="text-sm text-slate-300 mt-2 max-w-lg">A guided 8-step walkthrough of every library — what each field means, how to add/edit/delete safely, and rules of thumb for your team.</p>
          </div>
          <button onClick={() => setGuideOpen(true)} data-testid="help-start-libraries-tour" className="shrink-0 inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-blue-100 font-semibold text-sm px-5 py-2.5 rounded-sm transition-colors duration-200">
            <BookOpen size={16} weight="fill"/> Start tutorial <ArrowRight size={14}/>
          </button>
        </div>
      </div>

      <h2 className="font-display text-xl font-bold mt-8 mb-3">Quick reference</h2>

      <Section title="Who can do what?" testid="help-roles">
        <p>Every user in TNAI Sales Suite has one or more <b>roles</b>. Roles decide what they can do:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li><b>Admin</b> — manages users, edits libraries, sees all proposals, does everything.</li>
          <li><b>Sales</b> — creates proposals, uses AI drafting, computes pricing, submits for approval. Sees only their own proposals.</li>
          <li><b>Approver</b> — reviews and approves/rejects proposals submitted by sales. Sees all proposals.</li>
        </ul>
        <p className="mt-2">One user can hold multiple roles (e.g. the owner has all three).</p>
        <p className="mt-2 text-xs text-slate-500">Admins: create and manage users under <Link to="/users" className="underline">Users & Roles</Link>.</p>
      </Section>

      <Section title="What's in the Libraries and why they matter" testid="help-libraries">
        <p>The Libraries are the single source of truth. Every proposal is <i>composed</i> from library entries — nobody free-types clauses or feature names into a proposal. That means:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>Consistent language across every proposal you send.</li>
          <li>Change a price once and it applies to every future proposal.</li>
          <li>Legal-reviewed clauses can't be accidentally reworded by sales.</li>
        </ul>
        <p className="mt-3"><Link to="/library" className="text-blue-700 underline">Open the Libraries →</Link> or start the <button onClick={() => setGuideOpen(true)} className="text-blue-700 underline" data-testid="help-open-guide-inline">guided tutorial</button>.</p>
      </Section>

      <Section title="Creating a proposal (sales)">
        <p>1. Click <b>New Proposal</b> in the sidebar.</p>
        <p>2. Step through the 8-stage wizard: Deal Basics → Understanding → Features → Sites → Dependencies → Outcomes → Commercials → Review.</p>
        <p>3. On the last step, click <b>Create proposal</b>. You'll land on the proposal detail page.</p>
        <p>4. On the detail page: refine the prose (or click <b>AI draft</b> on any narrative section), fix any red BLOCK items in the Validation panel, then click <b>Submit</b>.</p>
        <p className="mt-2 text-xs text-slate-500">Tip: Validation is live — the panel updates as you edit.</p>
      </Section>

      <Section title="AI drafting — what it will and won't do">
        <p>The <b>AI draft</b> button (powered by Claude Sonnet) writes prose only for four sections: Executive Summary, Understanding, Pilot Outcome, and Site Schedule Notes.</p>
        <p>It's deliberately locked down:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>It won't invent numbers, prices, accuracies or dates.</li>
          <li>It won't write legal clauses (those come from the Library).</li>
          <li>It won't reference other clients by name.</li>
        </ul>
        <p className="mt-2">Every AI suggestion is a <b>proposal</b>, not a commitment — you must <b>Accept</b>, <b>Edit</b>, or <b>Reject</b> before it becomes part of the document.</p>
      </Section>

      <Section title="Validation & why exports get blocked">
        <p>Every proposal is checked against <b>14 BLOCK rules</b> and <b>5 WARN rules</b>. BLOCKs prevent export until fixed; WARNs are advisories.</p>
        <p>Common BLOCKs: missing client name, missing recipient, no features selected, no sites, empty Executive Summary, unresolved dependency answers, missing commercial terms for a commercial proposal.</p>
      </Section>

      <Section title="Exporting & versioning">
        <p>Click <b>DOCX</b> or <b>PDF</b> on the proposal page. On the first successful export:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>An immutable version snapshot is stored.</li>
          <li>The proposal status changes to <b>Issued</b>. Issued proposals cannot be edited.</li>
        </ul>
        <p className="mt-2">To make changes after issuing, create a new proposal or clone it (roadmap).</p>
      </Section>

      <Section title="The Audit Log">
        <p>Every action — logins, edits, approvals, exports, library changes — is written to the <Link to="/audit" className="text-blue-700 underline">Audit Log</Link>. You can always see who did what and when.</p>
      </Section>

      <LibraryGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}
